import TableBody from "@material-ui/core/TableBody";
import TableCell from "@material-ui/core/TableCell";
import TableFooter from "@material-ui/core/TableFooter";
import TableRow from "@material-ui/core/TableRow";
import Checkbox from "@saleor/components/Checkbox";
import ResponsiveTable from "@saleor/components/ResponsiveTable";
import Skeleton from "@saleor/components/Skeleton";
import TableCellHeader from "@saleor/components/TableCellHeader";
import TableHead from "@saleor/components/TableHead";
import TablePagination from "@saleor/components/TablePagination";
import useNavigator from "@saleor/hooks/useNavigator";
import { maybe, renderCollection } from "@saleor/misc";
import { ListProps } from "@saleor/types";
import React from "react";
import { FormattedMessage } from "react-intl";

import { BatchHistoryQuery_batchHistory_edges_node } from "../../types/BatchHistoryQuery";
import { batcHistoryDetailsPath } from "../../urls";
import {
  formatDate,
  formatDateWithTime,
  getBatchStatusValue
} from "../../utils/handlers";

interface BatchHistoryListProp extends ListProps {
  batchHistories: BatchHistoryQuery_batchHistory_edges_node[];
  disabled;
  isChecked;
  numberOfColumns?: number;
  selected;
  toggle;
  toggleAll;
}

const BatchHistoryList: React.FC<BatchHistoryListProp> = props => {
  const {
    batchHistories,
    disabled,
    isChecked,
    numberOfColumns,
    selected,
    toggle,
    toggleAll,
    settings,
    // sort,
    onNextPage,
    onPreviousPage,
    onUpdateListSettings,
    pageInfo
  } = props;

  const navigate = useNavigator();

  return (
    <ResponsiveTable>
      <TableHead
        colSpan={numberOfColumns}
        selected={selected}
        disabled={disabled}
        items={batchHistories}
        toggleAll={toggleAll}
      >
        <TableCellHeader>
          <FormattedMessage defaultMessage="Batch ID" />
        </TableCellHeader>
        <TableCellHeader>
          <FormattedMessage defaultMessage="Mall" />
        </TableCellHeader>
        <TableCellHeader>
          <FormattedMessage defaultMessage="Date Generated" />
        </TableCellHeader>
        <TableCellHeader>
          <FormattedMessage defaultMessage="Period Covered" />
        </TableCellHeader>
        <TableCellHeader>
          <FormattedMessage defaultMessage="Settlement Date" />
        </TableCellHeader>
        <TableCellHeader>
          <FormattedMessage defaultMessage="Batch Status" />
        </TableCellHeader>
      </TableHead>
      <TableBody>
        {batchHistories &&
          renderCollection(
            batchHistories,
            batchHistory => {
              const isSelected = batchHistory
                ? isChecked(batchHistory?.id)
                : false;

              return (
                <TableRow
                  key={batchHistory ? batchHistory.id : "skeleton"}
                  onClick={() =>
                    navigate(batcHistoryDetailsPath(batchHistory?.id))
                  }
                  hover
                >
                  <TableCell padding="checkbox">
                    <Checkbox
                      checked={isSelected}
                      disabled={disabled}
                      disableClickPropagation
                      onChange={() => toggle(batchHistory.id)}
                    />
                  </TableCell>
                  <TableCell>
                    {maybe(() => batchHistory.id, null) ? (
                      atob(batchHistory.id)
                    ) : (
                      <Skeleton />
                    )}
                  </TableCell>
                  <TableCell>
                    {maybe(() => batchHistory.mall_name, null) ? (
                      batchHistory.mall_name.toUpperCase()
                    ) : (
                      <Skeleton />
                    )}
                  </TableCell>
                  <TableCell>
                    {maybe(() => batchHistory.generated_at, null) ? (
                      formatDateWithTime(batchHistory.generated_at)
                    ) : (
                      <Skeleton />
                    )}
                  </TableCell>
                  <TableCell>
                    {maybe(() => batchHistory.coverage_start_date, null) ? (
                      `${formatDate(
                        batchHistory.coverage_start_date
                      )} - ${formatDate(batchHistory.coverage_end_date)}`
                    ) : (
                      <Skeleton />
                    )}
                  </TableCell>
                  <TableCell>
                    {maybe(() => batchHistory.credit_date, null) ? (
                      formatDate(batchHistory.credit_date)
                    ) : (
                      <Skeleton />
                    )}
                  </TableCell>
                  <TableCell>
                    {maybe(
                      () =>
                        batchHistory.cancelled_by && batchHistory.batch_status,
                      null
                    ) ? (
                      getBatchStatusValue(
                        batchHistory.batch_status,
                        batchHistory.cancelled_by
                      )
                    ) : (
                      <Skeleton />
                    )}
                  </TableCell>
                </TableRow>
              );
            },
            () => (
              <TableRow>
                <TableCell colSpan={numberOfColumns}>
                  <FormattedMessage defaultMessage="No orders found" />
                </TableCell>
              </TableRow>
            )
          )}
      </TableBody>
      <TableFooter>
        <TableRow>
          <TablePagination
            colSpan={numberOfColumns}
            settings={settings}
            hasNextPage={
              pageInfo && !disabled ? pageInfo.hasNextPage : undefined
            }
            onNextPage={onNextPage}
            onUpdateListSettings={onUpdateListSettings}
            hasPreviousPage={
              pageInfo && !disabled ? pageInfo.hasPreviousPage : undefined
            }
            onPreviousPage={onPreviousPage}
          />
        </TableRow>
      </TableFooter>
    </ResponsiveTable>
  );
};

export default BatchHistoryList;
